import { useEffect, useMemo } from 'react';
import { LinkableEntityType } from '@api';
import { AddActionIcon } from '@common';
import { SelectField } from '@hypeit-gmbh/hypeit-ui';
import { Grid, Group, Tooltip } from '@mantine/core';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CompanyLinkFormValuesType } from '../../../../types';
import {
  BUSINESS_FIELDS_GRID_COL_SPAN,
  BUSINESS_FIELDS_TRANS_PATH,
} from '../../constants';

type PersonSelectPropsType = {
  persons: LinkableEntityType[];
  onAddPerson?: () => void;
  preselectPersonId?: string | null;
};

export const PersonSelect: React.FC<PersonSelectPropsType> = ({
  persons,
  onAddPerson,
  preselectPersonId,
}) => {
  const { t } = useTranslation();

  const { setValue } = useFormContext<CompanyLinkFormValuesType>();

  useEffect(() => {
    if (!preselectPersonId) return;

    setValue('personId', preselectPersonId, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [preselectPersonId, setValue]);

  const selectData = useMemo(
    () =>
      persons.map((person) => ({
        value: person.id,
        label: person.name,
      })),
    [persons]
  );

  const addPersonActionLabel = t(
    `${BUSINESS_FIELDS_TRANS_PATH}.ADD_PERSON_BUTTON`
  );

  return (
    <Grid.Col span={BUSINESS_FIELDS_GRID_COL_SPAN}>
      <Group gap={1} align="flex-end">
        <SelectField<CompanyLinkFormValuesType>
          fieldName="personId"
          label={t(`${BUSINESS_FIELDS_TRANS_PATH}.PERSON_LABEL`)}
          data={selectData}
          required
          searchable
          flex={1}
        />
        {onAddPerson ? (
          <Tooltip label={addPersonActionLabel}>
            <AddActionIcon
              onClick={onAddPerson}
              ariaLabel={addPersonActionLabel}
              variant="subtle"
              mb={4}
            />
          </Tooltip>
        ) : null}
      </Group>
    </Grid.Col>
  );
};
